/* eslint-disable no-empty-pattern */
import flawService from '@/services/flaw.js';


function toNullableInt(value) {
  // Treat "", null, undefined as "not provided"
  if (value === '' || value === null || value === undefined) return undefined;

  // If already a number
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : undefined;
  }

  // If it's a string
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    // allow "12" or "12.0" etc -> int
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return undefined;
    return Math.trunc(num);
  }

  return undefined;
}

function sanitizeFlawPayload(flaw) {
  // shallow clone so we don't mutate caller objects
  const payload = { ...(flaw || {}) };

  // IMPORTANT: only send ints when valid
  const x = toNullableInt(payload.flawPositionX);
  const y = toNullableInt(payload.flawPositionY);

  if (x === undefined) delete payload.flawPositionX;
  else payload.flawPositionX = x;

  if (y === undefined) delete payload.flawPositionY;
  else payload.flawPositionY = y;

  // If you have other int fields that may be strings, normalize them too:
  // (Only if your backend expects ints)
  if ('flawScale' in payload) {
    const s = toNullableInt(payload.flawScale);
    if (s === undefined) delete payload.flawScale;
    else payload.flawScale = s;
  }

  // You can also remove UI-only fields that backend doesn't know about
  delete payload.fileLocalKey;
  delete payload.filePendingUpload;
  delete payload.source; // if backend doesn't accept it
  // delete payload.checklistQuestionId; // only delete if backend rejects unknown fields

  return payload;
}


export default {
	debug: false,
	namespaced: true,
	state: {
		flaws: [],
		flaw: {},
		flawStatistics: {},
		flawAttetions: {},
		searchParams: {
			start: '',
			stop: '',
			customerId: '',
			buildingId: '',
			done: null,
			orderBy: '',
			pageSize: 10,
			currentPage: 1
		},
		totalCount: 0,
		totalPages: 0
	},

	mutations: {
		SET_FLAWS(state, flaws) {
			state.flaws = flaws;
		},
		SET_FLAW(state, flaw) {
			state.flaw = flaw;
		},
		SET_PAGESIZE(state, pageSize) {
			state.searchParams.pageSize = pageSize;
		},
		SET_CURRENTPAGE(state, currentPage) {
			state.searchParams.currentPage = currentPage;
		},
		SET_TOTALPAGES(state, totalPages) {
			state.totalPages = totalPages;
		},
		SET_TOTALCOUNT(state, totalCount) {
			state.totalCount = totalCount;
		},
		SET_BUILDINGID(state, buildingId) {
			state.searchParams.buildingId = buildingId;
		},
		SET_FLAWSTATISTICS(state, flawStatistics) {
			state.flawStatistics = flawStatistics;
		},
		SET_FLAWATTETIONS(state, flawAttetions) {
			state.flawAttetions = flawAttetions;
		}
	},

	actions: {
		async getFlaw({ commit }, flawId) {
			return await flawService
				.getFlaw(flawId)
				.then(data => {
					commit('SET_FLAW', data);
				})
				.catch(err => {
					console.log('Error getting flaw: ', err);
				});
		},
		async getFlaws({ state, commit, dispatch, rootGetters }, params) {
			dispatch('appStore/setLoading', 1, { root: true });

			if (!state.searchParams.customerId) {
				state.searchParams.customerId = rootGetters['user/getUser'].customer.id;
			}

			state.searchParams.userBuildingId = rootGetters['buildingStore/getUserBuildingId'];

			return await flawService
				.getFlaws({ ...state.searchParams, ...params })
				.then(data => {
					const paginationValues = data._pagination;

					if (state.searchParams.currentPage <= paginationValues.totalPages || paginationValues.totalPages == 0) {
						commit('SET_FLAWS', data);
						commit('SET_CURRENTPAGE', paginationValues.currentPage);
						commit('SET_TOTALCOUNT', paginationValues.totalCount);
						commit('SET_TOTALPAGES', paginationValues.totalPages);
						dispatch('appStore/setLoading', -1, { root: true });
					} else {
						commit('SET_CURRENTPAGE', paginationValues.totalPages);
						dispatch('getFlaws');
						dispatch('appStore/setLoading', -1, { root: true });
					}
				})
				.catch(err => {
					console.log('Error getting flaws: ', err);
					dispatch('appStore/setLoading', -1, { root: true });
				});
		},

		async getFlawStatistic({ commit, dispatch, rootGetters }, params) {
			params.customerId = rootGetters['user/getActiveCustomer']?.id;
			params.userBuildingId = rootGetters['buildingStore/getUserBuildingId'];

			dispatch('appStore/setLoading', 1, { root: true });

			try {
				const data = await flawService.getFlawStatistic(params);
				dispatch('appStore/setLoading', -1, { root: true });
				commit('SET_FLAWSTATISTICS', data);
			} catch (error) {
				console.log(error);
			}
		},

		async createFlaw({}, flaw) {
			const payload = sanitizeFlawPayload(flaw);

			return await flawService
				.createFlaw(payload)
				.then(data => data)
				.catch(err => {
				console.log('Error creating flaw: ', err);
				throw err;
			});
		},

		async updateFlaw({}, flaw) {
			const payload = sanitizeFlawPayload(flaw);

			return await flawService
				.updateFlaw(payload)
				.then(data => data)
				.catch(err => {
				console.log('Error updating flaw: ', err);
				throw err;
				});
		},
		
		async deleteFlaw({ dispatch }, flaw) {
			return await flawService
				.deleteFlaw(flaw.id)
				.then(() => {
					dispatch('getFlaws');
				})
				.catch(err => {
					console.log('Error deleting flaw: ', err);
				});
		},

		// eslint-disable-next-line no-empty-pattern
		async triggerMail({}, flawId) {
			return await flawService
				.triggerFlawMail(flawId)
				.then(data => {
					return data;
				})
				.catch(err => {
					console.log('Error sending mail:', err);
				});
		},

		setFlaw({ commit }, flaw) {
			commit('SET_FLAW', flaw);
		},
		setFlaws({ commit }, flaws) {
			commit('SET_FLAW', flaws);
		},
		setCurrentPage({ commit }, currentPage) {
			commit('SET_CURRENTPAGE', currentPage);
		},
		setPageSize({ commit }, pageSize) {
			commit('SET_PAGESIZE', pageSize);
		},
		setTotalPages({ commit }, totalPages) {
			commit('SET_TOTALPAGES', totalPages);
		},
		setTotalCount({ commit }, totalCount) {
			commit('SET_TOTALCOUNT', totalCount);
		},
		setBuildingId({ commit }, buildingId) {
			commit('SET_BUILDINGID', buildingId);
		}
	},

	getters: {
		totalPages: state => {
			return state.totalPages;
		},
		totalCount: state => {
			return state.totalCount;
		},
		currentPage: state => {
			return state.searchParams.currentPage;
		},
		pageSize: state => {
			return state.searchParams.pageSize;
		},
		flaw: state => {
			return state.flaw;
		},
		flaws: state => {
			return state.flaws;
		},
		pagination: state => {
			return {
				currentPage: state.searchParams.currentPage,
				pageSize: state.searchParams.pageSize,
				totalPages: state.totalPages,
				totalCount: state.totalCount
			};
		},

		getFlawAttetionsData: state => {
			if (Object.keys(state.flawAttetions) < 1) return [];

			return [
				state.flawAttetions.totalFlawsSub14,
				state.flawAttetions.totalFlawsSub30,
				state.flawAttetions.totalFlawsOver30
			];
		}
	}
};
